import React, { useState, useEffect } from 'react';
import { ArrowLeft, Calculator } from 'lucide-react';
import { useCalculator } from '../context/CalculatorContext';
import { motion } from 'framer-motion';
import { getLanguageContent, getLanguageFromCurrency } from '../data/languages';
import CurrencySelector from './CurrencySelector';
import TraditionalCentralData from './traditionalCalculator/TraditionalCentralData';
import TraditionalSpecificData from './traditionalCalculator/TraditionalSpecificData';
import TraditionalImpact from './traditionalCalculator/TraditionalImpact';
import TraditionalResults from './traditionalCalculator/TraditionalResults';
import InvestmentAdvantages from './InvestmentAdvantages';

interface TraditionalCalculatorProps {
  onBack: () => void;
}

const TraditionalCalculator: React.FC<TraditionalCalculatorProps> = ({ onBack }) => {
  const { updateInvestment } = useCalculator();
  const { investment } = useCalculator();
  const [activeStep, setActiveStep] = useState<'currency' | 'centralData' | 'specificData' | 'advantages' | 'impact' | 'results'>('currency');
  
  // Get current language content
  const currentLanguage = investment.language || getLanguageFromCurrency(investment.currencyFormat);
  const content = getLanguageContent(currentLanguage);

  // Set fixed parameters for traditional view
  useEffect(() => {
    updateInvestment({
      reinvestProfits: false,
      additionalContributions: false,
      isLongTermCalculator: false,
      firstYearUtilityToUser: false
    });
  }, []); // Remove updateInvestment from dependencies to prevent infinite loop

  // Scroll to top when step changes
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [activeStep]);

  const handleNextStep = () => {
    switch (activeStep) {
      case 'currency':
        setActiveStep('centralData');
        break;
      case 'centralData':
        setActiveStep('specificData');
        break;
      case 'specificData':
        setActiveStep('results');
        break;
      case 'results':
        setActiveStep('advantages');
        break;
      case 'advantages':
        setActiveStep('impact');
        break;
      default:
        break;
    }
  };

  const handlePreviousStep = () => {
    switch (activeStep) {
      case 'centralData':
        setActiveStep('currency');
        break;
      case 'specificData':
        setActiveStep('centralData');
        break;
      case 'results':
        setActiveStep('specificData');
        break;
      case 'advantages':
        setActiveStep('results');
        break;
      case 'impact':
        setActiveStep('advantages');
        break;
      default:
        break;
    }
  };

  const handleReset = () => {
    setActiveStep('currency');
  };

  const renderStepContent = () => {
    switch (activeStep) {
      case 'currency':
        return <CurrencySelector onNext={handleNextStep} />;
      case 'centralData':
        return <TraditionalCentralData onNext={handleNextStep} />;
      case 'specificData':
        return <TraditionalSpecificData onNext={handleNextStep} onPrevious={handlePreviousStep} />;
      case 'results':
        return <TraditionalResults onNext={handleNextStep} onPrevious={handlePreviousStep} onReset={handleReset} />;
      case 'advantages':
        return <InvestmentAdvantages onNext={handleNextStep} onPrevious={handlePreviousStep} />;
      case 'impact':
        return <TraditionalImpact onNext={handleNextStep} onPrevious={handlePreviousStep} />;
      default:
        return <CurrencySelector onNext={handleNextStep} />;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-emerald-50">
      {/* Header */}
      <div className="bg-white/80 backdrop-blur-sm shadow-sm border-b border-white/20">
        <div className="max-w-7xl mx-auto px-6 py-6">
          <div className="flex items-center gap-6">
            <button
              onClick={onBack}
              className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100/50 rounded-xl transition-all duration-200"
              title="Volver al menú principal"
            >
              <ArrowLeft className="w-5 h-5" />
              <span className="font-medium">Volver</span>
            </button>
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-emerald-600 rounded-2xl flex items-center justify-center">
                <Calculator className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">{content.traditional.title}</h1>
                <p className="text-gray-600">{content.traditional.subtitle}</p>
              
              {/* Disclaimer */}
              <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                <p className="text-xs text-yellow-800">
                  <strong>⚠️ Aviso:</strong> Las proyecciones mostradas son estimaciones y no garantizan rendimientos futuros.
                </p>
              </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Progress Steps */}
      <div className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              {/* Step 0 - Currency Selection */}
              <button 
                onClick={() => setActiveStep('currency')}
                className={`flex flex-col items-center transition-all duration-200 hover:scale-105 ${activeStep === 'currency' ? 'text-green-600' : activeStep !== 'currency' ? 'text-green-400' : 'text-gray-400'}`}
                title="Paso 0: Selección de Moneda - Elige la moneda en la que deseas ver todos los resultados (MXN, USD, EUR) y configura el tipo de cambio correspondiente"
              >
                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                  activeStep === 'currency' ? 'bg-green-100 text-green-600 ring-2 ring-green-500' : 
                  activeStep !== 'currency' ? 'bg-green-50 text-green-500' : 'bg-gray-100'
                }`}>
                  <span className="text-sm font-bold">0</span>
                </div>
                <span className="text-xs mt-1">Moneda</span>
                <span className="text-xs mt-1">{content.traditional.steps.currency}</span>
              </button>
              
              {/* Connector Line 0-1 */}
              <div className={`h-0.5 w-10 mx-2 ${activeStep !== 'currency' ? 'bg-green-500' : 'bg-gray-200'}`}></div>
              
              {/* Step 1 - Datos Centrales */}
              <button 
                onClick={() => setActiveStep('centralData')}
                className={`flex flex-col items-center transition-all duration-200 hover:scale-105 ${activeStep === 'centralData' ? 'text-green-600' : activeStep !== 'centralData' ? 'text-green-400' : 'text-gray-400'}`}
                title="Paso 1: Datos Centrales - Configura el número de certificados iniciales (1-20), precio del certificado (200K-300K), horizonte de inversión (5-30 años) y parámetros de producción (kg/ha y precio/kg)"
              >
                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                  activeStep === 'centralData' ? 'bg-green-100 text-green-600 ring-2 ring-green-500' : 
                  activeStep !== 'centralData' ? 'bg-green-50 text-green-500' : 'bg-gray-100'
                }`}>
                  <span className="text-sm font-bold">1</span>
                </div>
                <span className="text-xs mt-1">Datos Centrales</span>
                <span className="text-xs mt-1">{content.traditional.steps.centralData}</span>
              </button>
              
              {/* Connector Line 2-3 */}
              <div className={`h-0.5 w-10 mx-2 ${activeStep !== 'centralData' ? 'bg-green-500' : 'bg-gray-200'}`}></div>
              
              {/* Step 2 - Datos Específicos */}
              <button 
                onClick={() => setActiveStep('specificData')}
                className={`flex flex-col items-center transition-all duration-200 hover:scale-105 ${activeStep === 'specificData' ? 'text-green-600' : activeStep === 'results' || activeStep === 'advantages' || activeStep === 'impact' ? 'text-green-400' : 'text-gray-400'}`}
                title="Paso 2: Datos Específicos - Ajusta la tasa de inflación (0-15%), activa/desactiva el incremento del precio del limón (0-10%), configura impuestos (0-50%) y establece las tasas de inversiones comparativas (CETES, Ahorro, Bienes Raíces)"
              >
                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                  activeStep === 'specificData' ? 'bg-green-100 text-green-600 ring-2 ring-green-500' : 
                  activeStep === 'results' || activeStep === 'advantages' || activeStep === 'impact' ? 'bg-green-50 text-green-500' : 'bg-gray-100'
                }`}>
                  <span className="text-sm font-bold">2</span>
                </div>
                <span className="text-xs mt-1">Datos Específicos</span>
                <span className="text-xs mt-1">{content.traditional.steps.specificData}</span>
              </button>
              
              {/* Connector Line 2-3 */}
              <div className={`h-0.5 w-10 mx-2 ${activeStep === 'results' || activeStep === 'advantages' || activeStep === 'impact' ? 'bg-green-500' : 'bg-gray-200'}`}></div>
              
              {/* Step 3 - Resultados */}
              <button 
                onClick={() => setActiveStep('results')}
                className={`flex flex-col items-center transition-all duration-200 hover:scale-105 ${activeStep === 'results' ? 'text-green-600' : activeStep === 'advantages' || activeStep === 'impact' ? 'text-green-400' : 'text-gray-400'}`}
                title="Paso 3: Resultados - Visualiza el resumen de tu inversión, métricas de rendimiento, evolución patrimonial, comparativas con otras inversiones y accede a la tabla de evolución detallada"
              >
                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                  activeStep === 'results' ? 'bg-green-100 text-green-600 ring-2 ring-green-500' : 
                  activeStep === 'advantages' || activeStep === 'impact' ? 'bg-green-50 text-green-500' : 'bg-gray-100'
                }`}>
                  <span className="text-sm font-bold">3</span>
                </div>
                <span className="text-xs mt-1">Resultados</span>
                <span className="text-xs mt-1">{content.traditional.steps.results}</span>
              </button>
              
              {/* Connector Line 3-4 */}
              <div className={`h-0.5 w-10 mx-2 ${activeStep === 'advantages' || activeStep === 'impact' ? 'bg-green-500' : 'bg-gray-200'}`}></div>
              
              {/* Step 4 - Ventajas */}
              <button 
                onClick={() => setActiveStep('advantages')}
                className={`flex flex-col items-center transition-all duration-200 hover:scale-105 ${activeStep === 'advantages' ? 'text-green-600' : activeStep === 'impact' ? 'text-green-400' : 'text-gray-400'}`}
                title="Paso 4: Ventajas de Cosecha Capital - Descubre por qué elegir Certificados de Plantación: activo real y productivo, ingresos dolarizados, doble beneficio, diversificación, impacto social y gestión profesional"
              >
                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                  activeStep === 'advantages' ? 'bg-green-100 text-green-600 ring-2 ring-green-500' : 
                  activeStep === 'impact' ? 'bg-green-50 text-green-500' : 'bg-gray-100'
                }`}>
                  <span className="text-sm font-bold">4</span>
                </div>
                <span className="text-xs mt-1">Ventajas</span>
                <span className="text-xs mt-1">{content.traditional.steps.advantages}</span>
              </button>
              
              {/* Connector Line 4-5 */}
              <div className={`h-0.5 w-10 mx-2 ${activeStep === 'impact' ? 'bg-green-500' : 'bg-gray-200'}`}></div>
              
              {/* Step 5 - Impacto */}
              <button 
                onClick={() => setActiveStep('impact')}
                className={`flex flex-col items-center transition-all duration-200 hover:scale-105 ${activeStep === 'impact' ? 'text-green-600' : 'text-gray-400'}`}
                title="Paso 5: Impacto de tu Inversión - Conoce el impacto ambiental (captura de CO₂, ahorro de agua), social (empleos generados, familias apoyadas) y económico (impulso local, contribución fiscal) de tu inversión"
              >
                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                  activeStep === 'impact' ? 'bg-green-100 text-green-600 ring-2 ring-green-500' : 'bg-gray-100'
                }`}>
                  <span className="text-sm font-bold">5</span>
                </div>
                <span className="text-xs mt-1">Impacto</span>
                <span className="text-xs mt-1">{content.traditional.steps.impact}</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-6 py-8">
        <motion.div 
          key={activeStep}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
          transition={{ duration: 0.5 }}
        >
          {renderStepContent()}
        </motion.div>
      </div>
    </div>
  );
};

export default TraditionalCalculator;